import { useEffect, useState } from 'react';

const SENSITIVITY_LEVELS = ['general', 'operational', 'management', 'executive'];

const SOURCE_LABELS = {
  ai_flagged:   { label: 'AI Flagged',      color: '#7c3aed', bg: '#ede9fe' },
  user_flagged: { label: 'User Submitted',  color: '#0369a1', bg: '#e0f2fe' },
};

const CATEGORY_COLORS = {
  safety:    '#dc2626',
  incident:  '#ea580c',
  policy:    '#0891b2',
  financial: '#d97706',
  client:    '#059669',
  general:   '#6b7280',
};

export default function KnowledgeApprovalPanel() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [actionState, setActionState] = useState({});
  const [expanded, setExpanded]   = useState(null);
  const [rejectReasons, setRejectReasons]         = useState({});
  const [sensitivityOverrides, setSensitivityOverrides] = useState({});

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const fetchPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/knowledge/pending', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const handleApprove = async (id) => {
    setActionState(s => ({ ...s, [id]: 'approving' }));
    try {
      const body = {};
      if (sensitivityOverrides[id]) body.sensitivityLevel = sensitivityOverrides[id];
      const res = await fetch(`/api/knowledge/${id}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setActionState(s => ({ ...s, [id]: 'done' }));
      setTimeout(() => setItems(prev => prev.filter(i => i._id !== id)), 600);
    } catch (e) {
      setActionState(s => ({ ...s, [id]: 'error' }));
    }
  };

  const handleReject = async (id) => {
    setActionState(s => ({ ...s, [id]: 'rejecting' }));
    try {
      const body = rejectReasons[id] ? { reason: rejectReasons[id] } : {};
      const res = await fetch(`/api/knowledge/${id}/reject`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setActionState(s => ({ ...s, [id]: 'done' }));
      setTimeout(() => setItems(prev => prev.filter(i => i._id !== id)), 600);
    } catch (e) {
      setActionState(s => ({ ...s, [id]: 'error' }));
    }
  };

  if (loading) return <div style={{ padding: 24, color: '#6b7280' }}>Loading pending items...</div>;
  if (error)   return <div style={{ padding: 24, color: '#dc2626' }}>Error: {error}</div>;

  if (items.length === 0) return (
    <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
      <div style={{ fontWeight: 600 }}>No pending items</div>
      <div style={{ fontSize: 13, marginTop: 4 }}>All flagged knowledge has been reviewed</div>
      <button onClick={fetchPending} style={{ marginTop: 16, padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer' }}>Refresh</button>
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Knowledge Approval Queue ({items.length})</h3>
        <button onClick={fetchPending} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 13 }}>Refresh</button>
      </div>

      {items.map(item => {
        const state = actionState[item._id];
        const isExpanded = expanded === item._id;
        const srcLabel = SOURCE_LABELS[item.source] || { label: item.source, color: '#6b7280', bg: '#f3f4f6' };
        const catColor = CATEGORY_COLORS[item.category] || '#6b7280';

        return (
          <div key={item._id} style={{
            border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12,
            opacity: state === 'done' ? 0.4 : 1, transition: 'opacity 0.4s',
            background: '#fff',
          }}>
            {/* Header */}
            <div
              style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              onClick={() => setExpanded(isExpanded ? null : item._id)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: srcLabel.bg, color: srcLabel.color }}>
                    {srcLabel.label}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', color: catColor, textTransform: 'capitalize' }}>
                    {item.category}
                  </span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', color: '#374151', textTransform: 'capitalize' }}>
                    {item.sensitivityLevel}
                  </span>
                </div>
                <div style={{ fontWeight: 500, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                  {new Date(item.createdAt).toLocaleString()} · by {item.submittedByRole}
                </div>
              </div>
              <span style={{ marginLeft: 12, color: '#9ca3af', fontSize: 18 }}>{isExpanded ? '▲' : '▼'}</span>
            </div>

            {/* Expanded body */}
            {isExpanded && (
              <div style={{ padding: '0 16px 16px' }}>
                <div style={{ background: '#f9fafb', borderRadius: 6, padding: 12, fontSize: 14, color: '#374151', marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                  {item.content}
                </div>

                {item.originalContext && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>AI Response Context:</div>
                    <div style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic', borderLeft: '3px solid #e5e7eb', paddingLeft: 10 }}>
                      {item.originalContext}
                    </div>
                  </div>
                )}

                {/* Sensitivity override */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Publish as:</label>
                  <select
                    value={sensitivityOverrides[item._id] || item.sensitivityLevel}
                    onChange={e => setSensitivityOverrides(s => ({ ...s, [item._id]: e.target.value }))}
                    style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                  >
                    {SENSITIVITY_LEVELS.map(l => <option key={l} value={l} style={{ textTransform: 'capitalize' }}>{l}</option>)}
                  </select>
                </div>

                {/* Reject reason */}
                <input
                  placeholder="Rejection reason (optional)"
                  value={rejectReasons[item._id] || ''}
                  onChange={e => setRejectReasons(r => ({ ...r, [item._id]: e.target.value }))}
                  style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', marginBottom: 12, boxSizing: 'border-box' }}
                />

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleApprove(item._id)}
                    disabled={state === 'approving' || state === 'done'}
                    style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: state === 'approving' ? '#d1fae5' : '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                  >
                    {state === 'approving' ? 'Approving...' : '✅ Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(item._id)}
                    disabled={state === 'rejecting' || state === 'done'}
                    style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: state === 'rejecting' ? '#fee2e2' : '#ef4444', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                  >
                    {state === 'rejecting' ? 'Rejecting...' : '❌ Reject'}
                  </button>
                </div>
                {state === 'error' && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>Action failed — try again</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
